import { useApiResource } from '@texas/api/hooks/useApiResource';
import { HttpClientResponse } from '@texas/api/httpClient/types';
import { useState, useRef, useCallback, useEffect } from 'react';

type FindResult<R> = R extends readonly (infer U)[] ? U : R;

export function useMetadataHook<A extends any[], R>(
  endpoint: (...args: A) => Promise<HttpClientResponse<R[]>>,
) {
  const { data, loading, refetch, error } = useApiResource(endpoint);

  const [hasFetchedData, setHasFetchedData] = useState(false);
  const requestedRef = useRef(false);

  const getData = useCallback(
    (...args: A) => {
      if (!requestedRef.current) {
        requestedRef.current = true;
        refetch(...args);
      }
    },
    [refetch],
  );

  useEffect(() => {
    if (!hasFetchedData && data) {
      setHasFetchedData(true);
    }
  }, [data, hasFetchedData]);

  const find = useCallback(
    (id: number): FindResult<R> | undefined => {
      if (!data) {
        return undefined;
      }

      if (data instanceof Array === false) {
        return undefined;
      }

      return (data as FindResult<R>[]).find((item: any) => item.id === id);
    },
    [data],
  );

  return [data ?? [], getData, loading, find, error] as const;
}
